import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Settings as SettingsIcon, AlertOctagon, Image as ImageIcon, Share2, Save, Loader2,
  Facebook, Instagram, Music2, Youtube, Unlink, Link2, Shield, Terminal,
  Video as VideoIcon, Globe, Mail,
} from "lucide-react";

const PLATFORM_META = {
  meta:      { label: "Meta / Facebook", icon: Facebook, color: "text-blue-400" },
  instagram: { label: "Instagram",       icon: Instagram, color: "text-pink-400" },
  tiktok:    { label: "TikTok",          icon: Music2,    color: "text-slate-200" },
  youtube:   { label: "YouTube",         icon: Youtube,   color: "text-red-400" },
};

const RHYTHM_OPTIONS = [
  { key: "monday",  label: "Montag",  hint: "einmal wöchentlich" },
  { key: "friday",  label: "Freitag", hint: "zum Wochenende" },
  { key: "daily",   label: "Täglich", hint: "jeden Tag 07:30" },
];
const RHYTHM_LABEL = Object.fromEntries(RHYTHM_OPTIONS.map(o => [o.key, o.label]));

export default function Einstellungen() {
  const [limits, setLimits] = useState({ max_images_per_month: 100, max_social_posts_per_month: 100 });
  const [usage, setUsage] = useState({ images_this_month: 0, social_posts_this_month: 0, images_remaining: 0, social_remaining: 0 });
  const [emergency, setEmergency] = useState(false);
  const [webAccess, setWebAccess] = useState(false);
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [emergencyDialog, setEmergencyDialog] = useState(false);
  const [setupDialog, setSetupDialog] = useState(null);
  const [disconnect, setDisconnect] = useState(null);
  const [digestRecipients, setDigestRecipients] = useState([]);  // [{email, rhythm}]
  const [newRecipient, setNewRecipient] = useState("");
  const [addingRecipient, setAddingRecipient] = useState(false);
  const [ownerEmail, setOwnerEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [mailStatus, setMailStatus] = useState(null);
  const [digestRhythm, setDigestRhythm] = useState("monday");
  const [digestNextHuman, setDigestNextHuman] = useState("");
  const [savingRhythm, setSavingRhythm] = useState(false);
  const [digestHistory, setDigestHistory] = useState([]);
  const [historyFilter, setHistoryFilter] = useState("");
  // Phase 15: SMTP form (password is write-only)
  const [smtpForm, setSmtpForm] = useState({ host: "", port: 587, user: "", password: "", sender: "" });
  const [smtpSaving, setSmtpSaving] = useState(false);
  const [smtpTesting, setSmtpTesting] = useState(false);
  const [smtpTestResult, setSmtpTestResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [u, e, p, w, dr, ms, rec, hist] = await Promise.all([
        api.get("/settings/usage").catch(() => ({ data: { limits: {}, usage: {} } })),
        api.get("/settings/emergency-stop").catch(() => ({ data: { active: false } })),
        api.get("/social/platforms").catch(() => ({ data: [] })),
        api.get("/settings/web-access").catch(() => ({ data: { active: false } })),
        api.get("/settings/digest-recipient").catch(() => ({ data: { rhythm: "monday", next_run_human: "Montag, 07:30 UTC" } })),
        api.get("/settings/smtp").catch(() => ({ data: { configured: false } })),
        api.get("/settings/digest-recipients").catch(() => ({ data: { recipients: [], owner_email: "" } })),
        api.get("/settings/digest-history").catch(() => ({ data: { entries: [] } })),
      ]);
      setUsage(u.data);
      setLimits(u.data.limits);
      setEmergency(!!e.data.active);
      setPlatforms(p.data);
      setWebAccess(!!w.data.active);
      setDigestRhythm(dr.data.rhythm || "monday");
      setDigestNextHuman(dr.data.next_run_human || "");
      setMailStatus(ms.data);
      setDigestRecipients(rec.data.recipients || []);
      setOwnerEmail(rec.data.owner_email || "");
      setDigestHistory(hist.data.entries || []);
      // Pre-fill SMTP form from server (never has password)
      setSmtpForm((f) => ({
        host: ms.data.host || "",
        port: ms.data.port || 587,
        user: ms.data.user || "",
        password: "",
        sender: ms.data.from || "",
      }));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const addRecipient = async () => {
    const email = newRecipient.trim();
    if (!email) return;
    setAddingRecipient(true);
    try {
      const { data } = await api.post("/settings/digest-recipients", { email });
      setDigestRecipients(data.recipients);
      setNewRecipient("");
      toast.success("Empfänger hinzugefügt");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Konnte Empfänger nicht hinzufügen");
    } finally { setAddingRecipient(false); }
  };

  const removeRecipient = async (email) => {
    try {
      const { data } = await api.delete(`/settings/digest-recipients?email=${encodeURIComponent(email)}`);
      setDigestRecipients(data.recipients);
      toast.success("Empfänger entfernt");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Konnte Empfänger nicht entfernen");
    }
  };

  const saveRhythm = async (rhythm) => {
    setSavingRhythm(true);
    const prev = digestRhythm;
    setDigestRhythm(rhythm);
    try {
      const { data } = await api.put("/settings/digest-rhythm", { rhythm });
      setDigestNextHuman(data.next_run_human || "");
      toast.success(`Rhythmus: ${RHYTHM_LABEL[rhythm]}`);
    } catch (err) {
      setDigestRhythm(prev);
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Konnte Rhythmus nicht speichern");
    } finally { setSavingRhythm(false); }
  };

  const updateRecipientRhythm = async (email, rhythm) => {
    try {
      const { data } = await api.patch(`/settings/digest-recipients?email=${encodeURIComponent(email)}`, { rhythm });
      setDigestRecipients(data.recipients);
      toast.success(`${email}: ${RHYTHM_LABEL[rhythm]}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Konnte Rhythmus nicht ändern");
    }
  };

  const refreshHistory = async () => {
    try {
      const { data } = await api.get("/settings/digest-history");
      setDigestHistory(data.entries || []);
    } catch {/* ignore */}
  };

  const sendTestMail = async () => {
    setSendingTest(true);
    try {
      const { data } = await api.post("/digest/send-test", {});
      const okCount = data.sent ?? 0;
      const failCount = data.failed ?? 0;
      if (data.ok) toast.success(`Test-Mail an ${okCount} Empfänger gesendet`);
      else if (okCount > 0) toast.warning(`${okCount} ok, ${failCount} fehlgeschlagen`);
      else toast.error(data.results?.[0]?.message || "Senden fehlgeschlagen");
      refreshHistory();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Senden fehlgeschlagen");
      refreshHistory();
    } finally { setSendingTest(false); }
  };

  const saveSmtp = async () => {
    if (!smtpForm.host.trim() || !smtpForm.sender.trim()) {
      return toast.error("Host und Absender-Adresse sind Pflichtfelder");
    }
    setSmtpSaving(true);
    try {
      const payload = {
        host: smtpForm.host.trim(),
        port: Number(smtpForm.port) || 587,
        user: smtpForm.user.trim(),
        sender: smtpForm.sender.trim(),
      };
      if (smtpForm.password) payload.password = smtpForm.password;
      const { data } = await api.put("/settings/smtp", payload);
      setMailStatus(data);
      setSmtpForm((f) => ({ ...f, password: "" }));
      toast.success("SMTP gespeichert");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "SMTP konnte nicht gespeichert werden");
    } finally { setSmtpSaving(false); }
  };

  const testSmtp = async () => {
    setSmtpTesting(true);
    setSmtpTestResult(null);
    try {
      const { data } = await api.post("/settings/smtp/test");
      setSmtpTestResult(data);
      if (data.ok) toast.success(data.message);
      else toast.error(data.message);
    } catch (err) {
      const m = formatApiErrorDetail(err.response?.data?.detail) || "Test fehlgeschlagen";
      setSmtpTestResult({ ok: false, message: m });
      toast.error(m);
    } finally { setSmtpTesting(false); }
  };

  const clearSmtp = async () => {
    try {
      const { data } = await api.delete("/settings/smtp");
      setMailStatus(data);
      setSmtpForm({ host: "", port: 587, user: "", password: "", sender: "" });
      setSmtpTestResult(null);
      toast.success("UI-Konfiguration gelöscht");
    } catch (err) {
      toast.error("Konnte Konfiguration nicht löschen");
    }
  };

  const saveLimits = async () => {
    setSaving(true);
    try {
      await api.put("/settings/limits", limits);
      toast.success("Limits aktualisiert");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fehler beim Speichern");
    } finally { setSaving(false); }
  };

  const toggleEmergency = async () => {
    try {
      const { data } = await api.post("/settings/emergency-stop/toggle");
      setEmergency(!!data.active);
      setEmergencyDialog(false);
      toast(data.active ? "Not-Stopp aktiviert" : "Not-Stopp deaktiviert");
    } catch (e) { toast.error("Not-Stopp konnte nicht umgeschaltet werden"); }
  };

  const toggleWeb = async () => {
    try {
      const { data } = await api.post("/settings/web-access/toggle");
      setWebAccess(!!data.active);
      toast(data.active ? "Web-Zugriff aktiviert" : "Web-Zugriff deaktiviert");
    } catch (e) { toast.error("Web-Zugriff konnte nicht umgeschaltet werden"); }
  };

  const connectPlatform = async (platform) => {
    try {
      const { data } = await api.post(`/social/${platform}/connect`);
      if (data.ok && data.configured && data.authorize_url) {
        window.open(data.authorize_url, "_blank", "noopener");
        toast("OAuth-Fenster geöffnet. Nach erfolgreicher Zustimmung erscheint das Konto hier.");
      } else {
        setSetupDialog({ platform, ...(platforms.find((p) => p.platform === platform) || {}) });
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Konnte Verbindung nicht starten");
    }
  };

  const confirmDisconnect = async () => {
    try {
      await api.delete(`/social/accounts/${disconnect.account.id}`);
      toast.success(`${PLATFORM_META[disconnect.platform]?.label} getrennt`);
      setDisconnect(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-[1200px] mx-auto" data-testid="settings-page">
      <header className="mb-6">
        <div className="text-xs font-semibold uppercase tracking-wider text-emerald-400">System</div>
        <h1 className="text-3xl font-extrabold tracking-tight mt-1 text-white">Einstellungen</h1>
        <p className="text-sm text-slate-400 mt-2">
          Sicherheitsschalter, monatliche Limits und verbundene Konten.
        </p>
      </header>

      {loading ? (
        <div className="text-slate-400">Lade…</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Spending limits */}
          <Card className="bg-[#111827] border-white/5" data-testid="limits-card">
            <CardContent className="p-6 space-y-5">
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-emerald-400" />
                <h2 className="font-bold text-white tracking-tight">Monatslimits</h2>
              </div>
              <p className="text-xs text-slate-400">
                Harte Obergrenzen pro Kalendermonat. Bei Erreichen blockiert der KI-Agent die Aktion –
                bis die Werte erhöht oder der Monat gewechselt wird.
              </p>

              <LimitField
                label="Max. Bildgenerierungen pro Monat"
                value={limits.max_images_per_month}
                onChange={(v) => setLimits((l) => ({ ...l, max_images_per_month: v }))}
                used={usage.images_this_month}
                remaining={usage.images_remaining}
                icon={ImageIcon}
                testid="limit-images"
              />
              <LimitField
                label="Max. Social-Beiträge pro Monat"
                value={limits.max_social_posts_per_month}
                onChange={(v) => setLimits((l) => ({ ...l, max_social_posts_per_month: v }))}
                used={usage.social_posts_this_month}
                remaining={usage.social_remaining}
                icon={Share2}
                testid="limit-social"
              />
              <LimitField
                label="Max. Video-Renderings pro Monat"
                value={limits.max_video_renders_per_month ?? 20}
                onChange={(v) => setLimits((l) => ({ ...l, max_video_renders_per_month: v }))}
                used={usage.video_renders_this_month ?? 0}
                remaining={usage.video_remaining ?? 20}
                icon={VideoIcon}
                testid="limit-video"
              />

              <Button onClick={saveLimits} disabled={saving} data-testid="save-limits-button"
                className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold">
                <Save className="h-4 w-4 mr-2" /> Limits speichern
              </Button>
            </CardContent>
          </Card>

          <Card className="bg-[#111827] border-white/5" data-testid="web-access-card">
            <CardContent className="p-6 space-y-5">
              <div className="flex items-center gap-2">
                <Globe className={`h-5 w-5 ${webAccess ? "text-emerald-400" : "text-slate-400"}`} />
                <h2 className="font-bold text-white tracking-tight">Web-Zugriff</h2>
              </div>
              <p className="text-xs text-slate-400">
                Globaler Master-Schalter für die Web-Tools (web_search, fetch_url). Standard: AUS.
                Bei Aktivierung darf der KI-Agent öffentliche URLs lesen und das Web durchsuchen –
                jede Anfrage wird im Protokoll festgehalten. Lokale/private IPs sind IMMER blockiert.
              </p>
              <div className={`rounded-lg p-4 border ${webAccess ? "bg-emerald-500/10 border-emerald-500/30" : "bg-slate-500/10 border-slate-500/20"}`}>
                <div className="text-sm font-semibold text-white mb-1">
                  Status: {webAccess ? "Aktiv – Recherche erlaubt" : "Inaktiv – Web-Tools blockieren Anfragen"}
                </div>
              </div>
              <Button onClick={toggleWeb} data-testid="web-toggle-button"
                className={webAccess
                  ? "w-full bg-amber-500 hover:bg-amber-600 text-white"
                  : "w-full bg-emerald-500 hover:bg-emerald-600 text-white"}>
                {webAccess ? "Web-Zugriff deaktivieren" : "Web-Zugriff aktivieren"}
              </Button>
            </CardContent>
          </Card>

          {/* Emergency stop */}
          <Card className="bg-[#111827] border-white/5" data-testid="emergency-card">
            <CardContent className="p-6 space-y-5">
              <div className="flex items-center gap-2">
                <AlertOctagon className={`h-5 w-5 ${emergency ? "text-red-400" : "text-slate-400"}`} />
                <h2 className="font-bold text-white tracking-tight">Not-Stopp</h2>
              </div>
              <p className="text-xs text-slate-400">
                Wenn aktiv, pausiert der Scheduler alle geplanten Skills und Social-Posts.
                Manuelle Ausführungen bleiben weiterhin möglich.
              </p>
              <div className={`rounded-lg p-4 border ${emergency ? "bg-red-500/10 border-red-500/30" : "bg-slate-500/10 border-slate-500/20"}`}>
                <div className="text-sm font-semibold text-white mb-1">
                  Status: {emergency ? "Aktiv – Automation pausiert" : "Inaktiv – Automation läuft"}
                </div>
              </div>
              <Button variant={emergency ? "outline" : "destructive"}
                onClick={() => setEmergencyDialog(true)} data-testid="emergency-toggle-button"
                className={emergency
                  ? "w-full border-white/10 bg-transparent text-slate-300 hover:bg-white/5"
                  : "w-full bg-red-500 hover:bg-red-600 text-white"}>
                {emergency ? "Not-Stopp deaktivieren" : "Not-Stopp aktivieren"}
              </Button>
            </CardContent>
          </Card>

          {/* Phase 13/14/15 — Email digest */}
          <Card className="bg-[#111827] border-white/5 lg:col-span-2" data-testid="digest-section">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-emerald-400" />
                <h2 className="font-bold text-white tracking-tight">E-Mail-Digest</h2>
              </div>
              <p className="text-xs text-slate-400">
                Der Wochen-Digest geht an alle Empfänger in dieser Liste.
              </p>

              <div>
                <Label className="text-slate-300 text-xs mb-1 block">Empfänger</Label>
                <div className="flex flex-wrap gap-2 mb-2" data-testid="digest-recipients-chips">
                  {digestRecipients.length === 0 && (
                    <span className="text-[11px] text-slate-500" data-testid="digest-recipients-empty">
                      Noch keine Empfänger — Fallback: dein Login ({ownerEmail || "—"}).
                    </span>
                  )}
                  {digestRecipients.map((r) => (
                    <span key={r.email}
                      className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 pl-2.5 pr-1 py-0.5 text-[11px] text-emerald-200"
                      data-testid={`digest-recipient-chip-${r.email}`}>
                      <span>{r.email}</span>
                      <select
                        value={r.rhythm}
                        onChange={(e) => updateRecipientRhythm(r.email, e.target.value)}
                        data-testid={`digest-recipient-rhythm-${r.email}`}
                        className="bg-transparent text-emerald-200 text-[10px] outline-none cursor-pointer appearance-none pr-1"
                        aria-label={`Rhythmus für ${r.email}`}>
                        {RHYTHM_OPTIONS.map(o => (
                          <option key={o.key} value={o.key} className="bg-slate-900">{o.label}</option>
                        ))}
                      </select>
                      <button onClick={() => removeRecipient(r.email)}
                        className="text-emerald-300 hover:text-rose-300 px-1"
                        data-testid={`digest-recipient-remove-${r.email}`}
                        aria-label={`${r.email} entfernen`}>×</button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input value={newRecipient} onChange={(e) => setNewRecipient(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addRecipient()}
                    placeholder="empfaenger@firma.de" type="email"
                    className="bg-white/5 border-white/10 text-slate-100"
                    data-testid="digest-recipient-input" />
                  <Button onClick={addRecipient} disabled={addingRecipient || !newRecipient.trim()}
                    className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold"
                    data-testid="digest-recipient-add">
                    {addingRecipient ? <Loader2 className="h-4 w-4 animate-spin" /> : "Hinzufügen"}
                  </Button>
                </div>
              </div>

              <div>
                <Label className="text-slate-300 text-xs mb-2 block">Rhythmus</Label>
                <div className="grid grid-cols-3 gap-2" data-testid="digest-rhythm-group">
                  {RHYTHM_OPTIONS.map(opt => {
                    const active = digestRhythm === opt.key;
                    return (
                      <button key={opt.key} type="button"
                        onClick={() => saveRhythm(opt.key)}
                        disabled={savingRhythm}
                        data-testid={`digest-rhythm-${opt.key}`}
                        aria-pressed={active}
                        className={`text-left rounded-lg border px-3 py-2 transition ${
                          active
                            ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-200"
                            : "border-white/10 bg-white/[0.02] text-slate-300 hover:bg-white/5"
                        }`}>
                        <div className="text-sm font-semibold">{opt.label}</div>
                        <div className="text-[10px] text-slate-500">{opt.hint}</div>
                      </button>
                    );
                  })}
                </div>
                {digestNextHuman && (
                  <div className="text-[11px] text-slate-400 mt-2" data-testid="digest-next-run">
                    Nächster Digest: {digestNextHuman}
                  </div>
                )}
              </div>

              <div className="rounded-md border border-white/5 bg-white/[0.02] p-3 text-xs">
                {mailStatus?.configured ? (
                  <span className="text-emerald-400" data-testid="digest-smtp-ok">
                    ✓ SMTP konfiguriert ({mailStatus.source === "ui" ? "UI" : ".env"}) · Absender: {mailStatus.from}
                  </span>
                ) : (
                  <span className="text-amber-300" data-testid="digest-smtp-missing">
                    ⚠ SMTP nicht konfiguriert — bitte unten einrichten.
                  </span>
                )}
              </div>

              <div className="flex flex-wrap gap-2">
                <Button onClick={sendTestMail} disabled={sendingTest}
                  variant="outline"
                  className="border-emerald-500/30 bg-transparent text-emerald-300 hover:bg-emerald-500/10 hover:text-emerald-200"
                  data-testid="digest-send-test">
                  {sendingTest ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Sende…</> : <>Test-Mail an alle senden</>}
                </Button>
                <Button onClick={async () => {
                  try {
                    const { data } = await api.post("/digest/run-now", {});
                    if (data.ok) toast.success(`Digest an ${data.sent} Empfänger ausgeführt`);
                    else toast.warning(`${data.sent} ok, ${data.failed} fehlgeschlagen`);
                  } catch (err) {
                    toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Ausführen fehlgeschlagen");
                  }
                  refreshHistory();
                }} variant="ghost"
                  className="text-slate-400 hover:text-emerald-300"
                  data-testid="digest-run-now">
                  Jetzt testweise ausführen
                </Button>
              </div>

              {/* Phase 16 — Delivery history */}
              <div className="pt-2" data-testid="digest-history-section">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-slate-300 text-xs">Letzte Zustellungen</Label>
                  <button onClick={refreshHistory}
                    data-testid="digest-history-refresh"
                    className="text-[10px] text-slate-500 hover:text-slate-300">↻ aktualisieren</button>
                </div>
                <div className="flex items-center gap-1 mb-2" data-testid="digest-history-filter">
                  {[
                    { k: "", l: "Alle" },
                    { k: "ok", l: "Erfolgreich" },
                    { k: "failed", l: "Fehlgeschlagen" },
                  ].map(opt => (
                    <button key={opt.k || "all"}
                      onClick={async () => {
                        setHistoryFilter(opt.k);
                        try {
                          const url = opt.k ? `/settings/digest-history?status=${opt.k}` : "/settings/digest-history";
                          const { data } = await api.get(url);
                          setDigestHistory(data.entries || []);
                        } catch { /* ignore */ }
                      }}
                      aria-pressed={historyFilter === opt.k}
                      data-testid={`digest-history-filter-${opt.k || "all"}`}
                      className={`text-[10px] px-2 py-0.5 rounded-full border transition ${
                        historyFilter === opt.k
                          ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-200"
                          : "border-white/10 bg-white/[0.02] text-slate-400 hover:bg-white/5"
                      }`}>
                      {opt.l}
                    </button>
                  ))}
                </div>
                {digestHistory.length === 0 ? (
                  <div className="text-[11px] text-slate-500 italic" data-testid="digest-history-empty">
                    Noch keine Digest-Mails versendet.
                  </div>
                ) : (
                  <div className="rounded-md border border-white/5 bg-white/[0.02] overflow-hidden">
                    <table className="w-full text-[11px]" data-testid="digest-history-table">
                      <thead className="bg-white/[0.03] text-slate-400 uppercase tracking-wider">
                        <tr>
                          <th className="text-left px-3 py-1.5 font-normal">Empfänger</th>
                          <th className="text-left px-3 py-1.5 font-normal">Zeitpunkt</th>
                          <th className="text-left px-3 py-1.5 font-normal">Ergebnis</th>
                        </tr>
                      </thead>
                      <tbody>
                        {digestHistory.map((h) => (
                          <tr key={h.id} className="border-t border-white/5"
                            data-testid={`digest-history-row-${h.id}`}>
                            <td className="px-3 py-1.5 text-slate-300">{h.recipient || "—"}</td>
                            <td className="px-3 py-1.5 text-slate-400">{h.created_at ? new Date(h.created_at).toLocaleString("de-DE") : ""}</td>
                            <td className="px-3 py-1.5">
                              {h.status === "ok" ? (
                                <span className="text-emerald-400">✓ Erfolgreich</span>
                              ) : h.status === "skipped" ? (
                                <span className="text-amber-300">⏸ Übersprungen · {h.message}</span>
                              ) : (
                                <span className="text-rose-300">✗ Fehlgeschlagen · {h.message}</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Phase 15 — SMTP configuration */}
          <Card className="bg-[#111827] border-white/5 lg:col-span-2" data-testid="smtp-section">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-emerald-400" />
                <h2 className="font-bold text-white tracking-tight">E-Mail-Versand (SMTP)</h2>
              </div>
              <p className="text-xs text-slate-400">
                Zugangsdaten für den Mailversand. Das Passwort bleibt serverseitig — es wird nie zurückgegeben.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                  <Label className="text-slate-300 text-xs mb-1 block">Host</Label>
                  <Input value={smtpForm.host} onChange={(e) => setSmtpForm((f) => ({ ...f, host: e.target.value }))}
                    placeholder="smtp.firma.de" className="bg-white/5 border-white/10 text-slate-100"
                    data-testid="smtp-host" />
                </div>
                <div>
                  <Label className="text-slate-300 text-xs mb-1 block">Port</Label>
                  <Input type="number" value={smtpForm.port}
                    onChange={(e) => setSmtpForm((f) => ({ ...f, port: Number(e.target.value) || 0 }))}
                    className="bg-white/5 border-white/10 text-slate-100"
                    data-testid="smtp-port" />
                </div>
                <div>
                  <Label className="text-slate-300 text-xs mb-1 block">Absender-Adresse</Label>
                  <Input value={smtpForm.sender} onChange={(e) => setSmtpForm((f) => ({ ...f, sender: e.target.value }))}
                    placeholder="digest@firma.de" type="email"
                    className="bg-white/5 border-white/10 text-slate-100"
                    data-testid="smtp-sender" />
                </div>
                <div>
                  <Label className="text-slate-300 text-xs mb-1 block">Benutzername</Label>
                  <Input value={smtpForm.user} onChange={(e) => setSmtpForm((f) => ({ ...f, user: e.target.value }))}
                    placeholder="(optional)" className="bg-white/5 border-white/10 text-slate-100"
                    data-testid="smtp-user" />
                </div>
                <div>
                  <Label className="text-slate-300 text-xs mb-1 block">
                    Passwort {mailStatus?.password_set && <span className="text-emerald-400 text-[10px]">· gesetzt</span>}
                  </Label>
                  <Input type="password" value={smtpForm.password}
                    onChange={(e) => setSmtpForm((f) => ({ ...f, password: e.target.value }))}
                    placeholder={mailStatus?.password_set ? "•••" : ""}
                    className="bg-white/5 border-white/10 text-slate-100"
                    data-testid="smtp-password" />
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button onClick={saveSmtp} disabled={smtpSaving}
                  className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold"
                  data-testid="smtp-save">
                  {smtpSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  Speichern
                </Button>
                <Button onClick={testSmtp} disabled={smtpTesting || !mailStatus?.configured}
                  variant="outline"
                  className="border-emerald-500/30 bg-transparent text-emerald-300 hover:bg-emerald-500/10"
                  data-testid="smtp-test">
                  {smtpTesting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Verbindung testen
                </Button>
                {mailStatus?.source === "ui" && (
                  <Button onClick={clearSmtp} variant="ghost"
                    className="text-slate-400 hover:text-rose-300"
                    data-testid="smtp-clear">
                    UI-Konfiguration löschen
                  </Button>
                )}
              </div>

              {smtpTestResult && (
                <div className={`rounded-md border p-2 text-xs ${
                  smtpTestResult.ok
                    ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200"
                    : "border-rose-500/30 bg-rose-500/10 text-rose-200"
                }`} data-testid="smtp-test-result">
                  {smtpTestResult.ok ? "✓" : "✗"} {smtpTestResult.message}
                </div>
              )}

              <details className="group">
                <summary className="cursor-pointer text-[11px] text-slate-500 hover:text-slate-300 select-none" data-testid="smtp-advanced-toggle">
                  Erweitert (Technisch)
                </summary>
                <div className="mt-2 text-[11px] text-slate-500 font-mono space-y-1">
                  <div>Quelle aktuell: {mailStatus?.source || "keine"}</div>
                  <div>UI-Werte überschreiben .env-Werte (SMTP_HOST, SMTP_PORT, SMTP_USER, SMTP_PASS, SMTP_FROM).</div>
                  <div>Port 465 → SSL, sonst STARTTLS.</div>
                </div>
              </details>
            </CardContent>
          </Card>

          {/* Connected accounts */}
          <Card className="bg-[#111827] border-white/5 lg:col-span-2" data-testid="accounts-card">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center gap-2">
                <Link2 className="h-5 w-5 text-emerald-400" />
                <h2 className="font-bold text-white tracking-tight">Verbundene Social-Konten</h2>
              </div>
              <p className="text-xs text-slate-400">
                OAuth-Credentials müssen in <code className="text-slate-300">/app/backend/.env</code> gesetzt sein,
                bevor eine Verbindung möglich ist. Zugangstokens werden ausschließlich maskiert dargestellt.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {platforms.map((p) => {
                  const meta = PLATFORM_META[p.platform];
                  const Icon = meta.icon;
                  return (
                    <div key={p.platform} className="rounded-lg border border-white/5 bg-white/[0.02] p-4"
                      data-testid={`account-row-${p.platform}`}>
                      <div className="flex items-center gap-3 mb-2">
                        <div className={`h-10 w-10 rounded-lg bg-white/5 grid place-items-center ${meta.color}`}>
                          <Icon className="h-5 w-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-white font-medium">{meta.label}</div>
                          <div className="text-[11px] text-slate-500">
                            {p.connected
                              ? <>verbunden · Token: <span className="font-mono">{p.account?.access_token_masked}</span></>
                              : p.configured
                                ? "env-Credentials vorhanden"
                                : `${p.missing_env_keys.length} env-Variablen fehlen`}
                          </div>
                        </div>
                        {p.connected ? (
                          <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 border font-normal">verbunden</Badge>
                        ) : p.configured ? (
                          <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30 border font-normal">bereit</Badge>
                        ) : (
                          <Badge className="bg-slate-500/15 text-slate-400 border-slate-500/30 border font-normal">unkonfiguriert</Badge>
                        )}
                      </div>

                      {p.connected ? (
                        <div className="flex gap-2">
                          <div className="flex-1 text-xs text-slate-300 truncate">{p.account?.display_name}</div>
                          <Button variant="ghost" size="sm" onClick={() => setDisconnect({ platform: p.platform, account: p.account })}
                            data-testid={`disconnect-${p.platform}`}
                            className="text-red-300 hover:text-red-200 hover:bg-red-500/10">
                            <Unlink className="h-3.5 w-3.5 mr-1" /> Trennen
                          </Button>
                        </div>
                      ) : p.configured ? (
                        <Button size="sm" onClick={() => connectPlatform(p.platform)}
                          data-testid={`connect-${p.platform}`}
                          className="bg-emerald-500 hover:bg-emerald-600 text-white w-full">
                          <Link2 className="h-3.5 w-3.5 mr-2" /> Konto verbinden
                        </Button>
                      ) : (
                        <Button variant="outline" size="sm" onClick={() => setSetupDialog(p)}
                          data-testid={`setup-${p.platform}`}
                          className="w-full border-white/10 bg-transparent text-slate-300 hover:bg-white/5 hover:text-white">
                          <Terminal className="h-3.5 w-3.5 mr-2" /> Setup-Anleitung
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Setup instructions modal */}
      <Dialog open={!!setupDialog} onOpenChange={(o) => !o && setSetupDialog(null)}>
        <DialogContent className="bg-[#111827] border-white/10 text-slate-100 max-w-xl max-h-[90vh] overflow-y-auto" data-testid="setup-dialog">
          {setupDialog && (
            <>
              <DialogHeader>
                <DialogTitle>Setup: {PLATFORM_META[setupDialog.platform]?.label}</DialogTitle>
                <DialogDescription className="text-slate-500">
                  OAuth-Credentials in /app/backend/.env konfigurieren – dann erscheint hier ein Verbinden-Button.
                  Keine Fake-Buttons: ohne Credentials ist keine Verbindung möglich.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 text-sm">
                <div>
                  <div className="text-xs text-slate-400 uppercase tracking-wider mb-2">Schritte</div>
                  <ol className="list-decimal list-inside space-y-1 text-slate-300">
                    {(setupDialog.setup?.steps || []).map((s, i) => <li key={i}>{s}</li>)}
                  </ol>
                </div>
                <div>
                  <div className="text-xs text-slate-400 uppercase tracking-wider mb-2">Benötigte .env-Keys</div>
                  <div className="rounded bg-black/40 border border-white/5 p-3 font-mono text-[11px] text-slate-300 whitespace-pre">
                    {(setupDialog.env_keys || []).map((k) => `${k}=""`).join("\n")}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-slate-400 uppercase tracking-wider mb-2">OAuth-Redirect-URI</div>
                  <code className="block rounded bg-black/40 border border-white/5 p-2 text-[11px] text-emerald-300 break-all">
                    {setupDialog.callback_url}
                  </code>
                </div>
                {setupDialog.setup?.console_url && (
                  <a href={setupDialog.setup.console_url} target="_blank" rel="noreferrer"
                    className="inline-block text-xs text-emerald-400 hover:text-emerald-300">
                    → Developer-Portal öffnen
                  </a>
                )}
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setSetupDialog(null)}>Schließen</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Emergency stop confirm */}
      <AlertDialog open={emergencyDialog} onOpenChange={setEmergencyDialog}>
        <AlertDialogContent className="bg-[#111827] border-white/10 text-slate-100">
          <AlertDialogHeader>
            <AlertDialogTitle>{emergency ? "Not-Stopp deaktivieren?" : "Not-Stopp aktivieren?"}</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              {emergency
                ? "Scheduler und automatische Social-Veröffentlichungen laufen wieder."
                : "Alle geplanten Skills und terminierten Social-Beiträge werden sofort pausiert."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="emergency-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={toggleEmergency} data-testid="emergency-confirm"
              className={emergency ? "bg-emerald-500 hover:bg-emerald-600" : "bg-red-500 hover:bg-red-600"}>
              Bestätigen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Disconnect confirm */}
      <AlertDialog open={!!disconnect} onOpenChange={(o) => !o && setDisconnect(null)}>
        <AlertDialogContent className="bg-[#111827] border-white/10 text-slate-100">
          <AlertDialogHeader>
            <AlertDialogTitle>Verbindung trennen?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Das verbundene {disconnect && PLATFORM_META[disconnect.platform]?.label}-Konto wird entfernt.
              Künftige Beiträge für diese Plattform werden als manuell nötig markiert.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="disconnect-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDisconnect} data-testid="disconnect-confirm"
              className="bg-red-500 hover:bg-red-600">Trennen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function LimitField({ label, value, onChange, used, remaining, icon: Icon, testid }) {
  const pct = value > 0 ? Math.min(100, Math.round((used / value) * 100)) : 100;
  const warn = pct >= 80;
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <Label className="text-slate-300 text-xs flex items-center gap-1.5">
          <Icon className="h-3.5 w-3.5" /> {label}
        </Label>
        <span className={`text-[11px] font-mono ${warn ? "text-amber-400" : "text-slate-500"}`}>
          {used} / {value} · {remaining} übrig
        </span>
      </div>
      <Input type="number" min="0" max="100000" value={value}
        onChange={(e) => onChange(Number(e.target.value) || 0)}
        className="bg-white/5 border-white/10 text-slate-100"
        data-testid={`${testid}-input`} />
      <div className="h-1.5 bg-white/5 rounded-full overflow-hidden mt-1.5">
        <div className={`h-full ${warn ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
